import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import TaskCard from '../components/TaskCard';
import NewTaskDialog from '../components/NewTaskDialog';
import DailyDigestBanner from '../components/DailyDigestBanner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function Dashboard({ scope = 'all' }) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks?scope=${scope}`);
      setTasks(data);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      const statusOk = statusFilter === 'all' || t.status === statusFilter;
      const q = query.toLowerCase();
      const qOk = !q || t.title.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q);
      return statusOk && qOk;
    });
  }, [tasks, query, statusFilter]);

  const stats = useMemo(() => ({
    total: tasks.length,
    open: tasks.filter(t => t.status === 'open').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    resolved: tasks.filter(t => t.status === 'resolved').length,
  }), [tasks]);

  const scopeTitles = {
    all: 'ALL VISIBLE TASKS',
    mine: 'ASSIGNED TO ME',
    general: 'GENERAL / BROADCAST',
    created: 'CREATED BY ME',
  };

  return (
    <Layout onNewTask={() => setDialogOpen(true)}>
      <div className="space-y-6">
        {scope === 'all' && (user?.role === 'boss' || user?.role === 'manager') && <DailyDigestBanner />}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">DASHBOARD</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">
            {scopeTitles[scope] || 'TASKS'}
          </h1>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { k: 'TOTAL', v: stats.total, bg: 'bg-white', text: 'text-zinc-900' },
            { k: 'OPEN', v: stats.open, bg: 'bg-white', text: 'text-zinc-900' },
            { k: 'IN PROGRESS', v: stats.in_progress, bg: 'bg-[#FFD700]', text: 'text-zinc-900' },
            { k: 'RESOLVED', v: stats.resolved, bg: 'bg-black', text: 'text-white' },
          ].map((s) => (
            <div key={s.k} data-testid={`stat-${s.k.toLowerCase().replace(/ /g,'-')}`} className={`${s.bg} ${s.text} border border-zinc-900 p-5`}>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">{s.k}</div>
              <div className="font-display text-5xl font-black tracking-tighter leading-none mt-2">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 border-t border-b border-zinc-200 py-4">
          <input
            data-testid="search-input"
            type="search"
            placeholder="Search tasks..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 min-w-[200px] border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-2 bg-white"
          />
          <div className="flex items-center gap-1">
            {['all', 'open', 'in_progress', 'resolved'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`filter-${s}`}
                className={`px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] border ${statusFilter === s ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900'}`}
              >
                {s.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Task grid */}
        {renderTaskGrid({ loading, filtered, user })}
      </div>
      <NewTaskDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onCreated={load} />
    </Layout>
  );
}

function renderTaskGrid({ loading, filtered, user }) {
  if (loading) return <div className="text-sm text-zinc-500 font-mono">LOADING...</div>;
  if (filtered.length === 0) {
    const msg = user?.role === 'employee'
      ? 'Waiting for your boss to assign you work.'
      : 'Click "New Task" to create one.';
    return (
      <div className="border border-dashed border-zinc-300 p-12 text-center" data-testid="empty-state">
        <div className="font-display text-2xl font-black mb-2">No tasks here yet.</div>
        <div className="text-sm text-zinc-600">{msg}</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {filtered.map((t) => <TaskCard key={t.id} task={t} assigneeNames={t.assignee_ids} />)}
    </div>
  );
}
